import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { enUS } from "date-fns/locale";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import moment from "moment/moment";
import { FiLoader } from "react-icons/fi";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { useNotification } from "../../contextApi/NotificationProvider";
import {
  getDownloadExcelData,
  getReport,
  sendNotification,
} from "../../redux/ReduxSlices/candidateSlice";
import { getHistoryData } from "../../redux/ReduxSlices/managerSlice";

const assessments = [
  { id: "a1", name: "Tech Skills Assessment", category: "Technical" },
  { id: "a2", name: "New Joiner Onboarding", category: "HR" },
];

const getAssessmentName = (id) =>
  assessments.find((a) => a.id === id)?.name || "Unknown";
const getAssessmentCategory = (id) =>
  assessments.find((a) => a.id === id)?.category || "Unknown";

const AssessmentRecordsPage = () => {
  const [filter, setFilter] = useState("All");
  const { showNotification } = useNotification();
  const [range, setRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { historyData, loading, error } = useSelector((state) => state.manager);
  const [activeTab, setActiveTab] = useState("own");
  const startDate = range[0].startDate;
  const endDate = range[0].endDate;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [containerHeight, setContainerHeight] = useState(300);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(getHistoryData());
  }, [dispatch]);

  const sendNotificationEmail = async (candidateId) => {
    setIsLoading(true);

    try {
      const payload = {
        candidate_id: candidateId,
      };

      const response = await dispatch(sendNotification(payload)).unwrap();

      showNotification("The candidate has been successfully notified to complete the assessment", "success", 8000);

      dispatch(getHistoryData());
      setIsLoading(false);

    } catch (error) {
      console.log("error", error);
      showNotification("Something went wrong!", "error", 3000);
      setIsLoading(false);
    }
  };



  const handleExport = async () => {
    const exportData = [];
    try {

      const response = await dispatch(getDownloadExcelData()).unwrap()
      if (response.success) {

        // filteredDataByDate.forEach((entry) => {
        response.export_data.forEach((candidate) => {
          const rawUTC = candidate.exam_completed_at;
          const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const utcDate = new Date(rawUTC.endsWith("Z") ? rawUTC : rawUTC.replace(" ", "T") + "Z");
          const localDate = utcToZonedTime(utcDate, userTimeZone);
          const formattedDate = format(localDate, "dd MMM yyyy, hh:mm a");

          const fullName = candidate.first_name && candidate.last_name
            ? `${capitalize(candidate.first_name)} ${capitalize(candidate.last_name)}`
            : candidate.email || "-";

          const reportLink = `https://portal.selfsync.ai/view-candidate-report/${candidate.candidate_pk}`;

          const examStatus = candidate.current_role_fit_score === null
            ? "Not Completed"
            : "Completed";

          const assessmentFor = candidate.is_new_joiner === true && candidate.is_existing_emp === false
            ? "New Hires"
            : candidate.is_new_joiner === false && candidate.is_existing_emp === true
              ? "Existing Employee"
              : "-";

          const reportingManager = candidate.is_new_joiner === false
            ? candidate.manager_first_name && candidate.manager_last_name
              ? `${capitalize(candidate.manager_first_name)} ${capitalize(candidate.manager_last_name)}`
              : candidate.manager_email || "-"
            : "NA";

          const row = {
            "Assessment Date": formattedDate,
            "Candidate Name / Email ": fullName,
            "Score": candidate.final_score !== null ? candidate.final_score : "-",
            "Manager Name / Email": reportingManager,
            "Candidate Report Link": reportLink,
            "Assessment Type": assessmentFor,
          };

          // Step 2: Append each trait as its own column
          candidate.traits.forEach(trait => {
            if (trait.trait) {
              row[trait.trait] = trait.score;
            }
          });

          exportData.push(row);


        });
        // });


        const worksheet = XLSX.utils.json_to_sheet(exportData.reverse());
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Assessment Records");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(data, "assessment_records.xlsx");
      }


    } catch (error) {
      console.log("error", error)
    }
  };


  const filteredDataByDate = historyData.filter((entry) => {
    const entryDate = new Date(entry._id.date.split(" ")[0]);
    const fromDate = startDate ? new Date(startDate) : null;
    const toDate = endDate ? new Date(endDate) : null;

    if (entryDate) entryDate.setHours(0, 0, 0, 0);
    if (fromDate) fromDate.setHours(0, 0, 0, 0);
    if (toDate) toDate.setHours(0, 0, 0, 0);

    return (
      (!fromDate || entryDate >= fromDate) && (!toDate || entryDate <= toDate)
    );
  });

  useEffect(() => {
    const updateHeight = () => {
      if (window.innerWidth < 768) {
        setContainerHeight(250); // Mobile
      } else {
        setContainerHeight(300); // Desktop / Tablet
      }
    };

    updateHeight(); // Set initially

    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Group by date and count candidates
  const chartData = Object.values(
    filteredDataByDate.reduce((acc, entry) => {
      const date = entry._id.date.split(" ")[0]; // YYYY-MM-DD
      if (!acc[date]) {
        acc[date] = { date, count: 0 };
      }
      acc[date].count += entry.candidates.length;
      return acc;
    }, {})
  ).sort((a, b) => new Date(a.date) - new Date(b.date)); // optional: sort by date

  const handleReport = (candidate) => {
    const id = candidate.candidate_pk;
    dispatch(getReport(id));
    navigate(`/viewreport/${id}`);
  };


  const sendButtonClicked = () => {
    navigate('/assessments')
  }

  return (
    <div className="min-h-screen p-6 max-md:p-0">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
          <FiLoader className="animate-spin text-[#822bda] text-4xl" />
        </div>
      )}

      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
          <FiLoader className="animate-spin text-[#822bda] text-4xl" />
        </div>
      )}

      {filteredDataByDate.length === 0 ?
        <></>
        :
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 max-md:p-2 mb-6 mt-4"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h2 className="text-lg font-semibold text-gray-700 mb-4 px-2">
            Assessment Dispatch Trend
          </h2>

          <div className="w-full" style={{ height: containerHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.5} />
                    <stop offset="70%" stopColor="#7C3AED" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" minTickGap={20} />
                <YAxis allowDecimals={false} domain={["auto", "auto"]} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#7C3AED"
                  fill="url(#lineGradient)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      }

      {/* Date Range Picker */}
      <motion.div
        className="mt-10 max-md:mt-4 max-md:mb-2"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="flex items-center justify-between max-md:justify-center max-md:flex-col">
          {/* Score Brackets - Desktop */}
          <div className="flex gap-4 mr-4 max-md:hidden">
            {/* Growing - Developing */}
            <h3 className="text-xs font-bold text-gray-700">Performance Bands</h3>
            <div className="flex items-center gap-1">
              <div className="flex items-center gap-1 min-w-12">
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <span className="text-xs font-bold text-gray-700">0-4.9</span>
              </div>
              <span className="text-xs text-gray-600">Growing - Developing</span>
            </div>

            {/* Established */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 min-w-12">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-xs font-bold text-gray-700">5.0-7.9</span>
              </div>
              <span className="text-xs text-gray-600">Established - Consistent</span>
            </div>

            {/* Distinctive */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 min-w-12">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-xs font-bold text-gray-700">8.0-10</span>
              </div>
              <span className="text-xs text-gray-600">Distinctive - Stands out</span>
            </div>
          </div>

          {/* Score Brackets - Mobile */}
          <div className="md:hidden mr-3">
            <div className="flex items-center gap-3 p-2 border border-gray-300 rounded-md bg-white shadow-sm mb-4">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                  <span className="text-xs font-bold text-gray-700">0-4.9   Growing - Developing</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-xs font-bold text-gray-700">5.0-7.9  Established - Consistent</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-xs font-bold text-gray-700">8.0-10  Distinctive - Stands out</span>
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => setIsModalOpen(true)}
              className="text-black px-4 max-md:px-1.5 py-1 rounded-md text-xs font-medium transition-all duration-200 shadow border bg-violet-50 border-gray-300"
            >
              Select Date Range
            </button>
            <button
              onClick={() => {
                setRange([
                  { startDate: null, endDate: null, key: "selection" },
                ]);
                setIsModalOpen(false);
              }}
              className="text-black px-4 max-md:px-1.5 py-1 rounded-md text-xs font-medium transition-all duration-200 shadow border bg-violet-50 border-gray-300"
            >
              Clear Filter
            </button>
            <div className="relative group inline-block">
              <button
                onClick={handleExport}
                className="text-green-600 px-4 max-md:px-1.5 py-1 rounded-md text-xs font-medium transition-all duration-200 shadow border bg-violet-50 border-gray-300"
              >
                Export Result
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 -top-10 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                Download organization's full<br /> assessment data
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 shadow-xl w-full max-w-2xl">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Select Date Range
            </h3>
            <DateRange
              editableDateInputs={false}
              onChange={(item) => setRange([item.selection])}
              moveRangeOnFirstSelection={false}
              ranges={range}
              className="rounded-xl shadow"
              maxDate={new Date()}
              locale={enUS}
              showDateDisplay={false}
            />
            <div className="flex justify-end mt-6 gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm shadow"
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filtered Table */}
      <motion.div
        className="max-w-8xl mx-auto py-4 max-md:px-0"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {filteredDataByDate.length === 0 ? (
          <div className="text-center text-gray-500 mt-32 text-sm my-10">
            <p className="text-center text-2xl text-gray-500 mt-32 mb-4">
              No assessment records found
            </p>
            <p className="text-gray-400 mb-4">
              Assign the new assessment to the assessee. Once done, you’ll see all the details and the report appear here
            </p>
            <button className="mt-2 inline-flex items-center gap-2 px-6 py-1 border border-violet-400 text-violet-600 text-sm font-semibold rounded-md shadow-md hover:bg-violet-500 hover:text-white hover:shadow-lg transition-all duration-200 ease-in-out active:scale-95"
              onClick={sendButtonClicked}
            >
              Starts New Assessments
            </button>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead>
                <tr className="bg-purple-100 text-sm">
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Score</th>
                  <th className="p-2 text-left">Has Started</th>
                  <th className="p-2 text-left">Has Completed</th>
                  <th className="p-2 text-left">Report Status</th>
                  <th className="p-2 text-left">Reporting Manager</th>
                  <th className="p-2 text-left">Notify</th>
                  <th className="p-2 text-left">View Report</th>
                </tr>
              </thead>
              <tbody>
                {filteredDataByDate.map((entry, index) => {
                  const rawUTC = entry._id.date;
                  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                  // Force parse as UTC
                  const utcDate = new Date(
                    rawUTC.endsWith("Z") ? rawUTC : rawUTC.replace(" ", "T") + "Z"
                  );
                  // Convert to local time zone
                  const localDate = utcToZonedTime(utcDate, userTimeZone);
                  // Format as per local time
                  const formattedDate = format(localDate, "dd MMM yyyy, hh:mm a");

                  const showManagerColumn = entry.candidates.some(
                    (candidate) => candidate.is_new_joiner === false
                  );

                  const showReportColumn = entry.candidates.some(
                    (candidate) => candidate.is_report_generated === "success"
                  );

                  const pastTime = moment(formattedDate, "DD MMM YYYY, hh:mm a");
                  const now = moment();
                  const diffInHours = now.diff(pastTime, "hours");

                  const showNotifyButton = entry.candidates.some(
                    (candidate) =>
                      candidate.is_assessment_completed == false
                    // &&
                    // candidate.email_reminder_count == 0 &&
                    // diffInHours > 3
                  );

                  return (
                    <React.Fragment key={index}>
                      {entry.candidates.map((candidate) => (
                        <tr
                          key={candidate.candidate_pk}
                          className="border-t border-gray-200 text-sm hover:bg-gray-50"
                        >
                          <td className="p-3">{formattedDate}</td>
                          <td className="p-3">
                            {candidate.first_name && candidate.last_name
                              ? `${capitalize(
                                candidate.first_name
                              )} ${capitalize(candidate.last_name)}`
                              : candidate.email === null
                                ? "-"
                                : candidate.email}
                          </td>
                          <td className="p-3">
                            {candidate.current_role_fit_score === null
                              ? "-"
                              : candidate.current_role_fit_score}
                          </td>
                          <td className="p-3">
                            {candidate.is_assessment_started === false ? "No" : "Yes"}
                          </td>
                          <td className="p-3">
                            {candidate.is_assessment_completed === false ? "No" : "Yes"}
                          </td>
                          <td className="p-3">
                            <span
                              className={`px-3 max-md:px-1 py-1 rounded-full text-sm max-md:text-xs font-medium ${candidate.is_report_generated === "pending"
                                ? "bg-yellow-400 text-white"
                                : candidate.is_report_generated === "processing"
                                  ? "bg-blue-400 text-white"
                                  : candidate.is_report_generated === "success"
                                    ? "bg-green-500 text-white"
                                    : candidate.is_report_generated === "failed"
                                      ? "bg-red-500 text-white"
                                      : ""
                                }`}
                            >
                              {candidate.is_report_generated
                                .charAt(0)
                                .toUpperCase() +
                                candidate.is_report_generated.slice(1)}
                            </span>
                          </td>
                          <td className="p-3">
                            {showManagerColumn
                              ? candidate.manager_first_name && candidate.manager_last_name
                                ? `${capitalize(
                                  candidate.manager_first_name
                                )} ${capitalize(candidate.manager_last_name)}`
                                : candidate.manager_email === null
                                  ? "-"
                                  : candidate.manager_email
                              : "NA"}
                          </td>
                          <td className="p-3">
                            {candidate.is_assessment_completed == false ? (
                              <button
                                className="btn-gradient px-3 max-md:px-1 py-1 rounded-full text-sm max-md:text-xs font-medium text-[#fbf8ff]"
                                onClick={() =>
                                  sendNotificationEmail(candidate?.candidate_pk)
                                }
                              >
                                Notify
                              </button>
                            ) : (
                              <button className="px-3 max-md:px-1 py-1 rounded-full text-sm max-md:text-xs font-medium">
                                -
                              </button>
                            )}
                          </td>
                          <td className="p-3">
                            {candidate.is_report_generated === "success" ? (
                              <span
                                className="text-green-700 font-bold text-sm max-md:text-xs cursor-pointer"
                                onClick={() => handleReport(candidate)}
                              >
                                View Report
                              </span>
                            ) : (
                              <span className="text-sm max-md:text-xs font-semibold text-gray-500">
                                Pending
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AssessmentRecordsPage;
